import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Accordion } from '@/components/ui/Accordion'
import { DiscountBox, type AppliedDiscount } from '@/components/checkout/DiscountBox'
import { useCartStore } from '@/stores/cartStore'
import { useScentProfileStore } from '@/stores/scentProfileStore'
import { useUIStore } from '@/stores/uiStore'
import { useCatalogue } from '@/hooks/useCatalogue'
import {
  createOrder,
  verifyOrder,
  estimateDelivery,
  getLogisticsQuotes,
  previewDiscount,
  type DeliveryEstimate,
  type ShippingQuote,
} from '@/lib/api'
import { formatPrice } from '@/lib/utils'
import { Truck, Check } from 'lucide-react'

export function Checkout() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { items, total, giftWrap, clearCart } = useCartStore()
  const addOwned = useScentProfileStore((s) => s.addOwned)
  const addToast = useUIStore((s) => s.addToast)
  const { products } = useCatalogue()

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    lga: '',
    state: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [delivery, setDelivery] = useState<DeliveryEstimate | null>(null)
  const [deliveryLoading, setDeliveryLoading] = useState(false)
  const [quotes, setQuotes] = useState<ShippingQuote[] | null>(null)
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [quotesError, setQuotesError] = useState<string | null>(null)
  const [selectedQuote, setSelectedQuote] = useState<ShippingQuote | null>(null)
  const [applied, setApplied] = useState<AppliedDiscount | null>(null)

  const pendingRef =
    [...searchParams.getAll('reference'), ...searchParams.getAll('trxref')].find(
      (value) => value.trim() !== ''
    ) || null

  useEffect(() => {
    if (!pendingRef) return
    setVerifying(true)
    let attempts = 0
    const maxAttempts = 10
    const interval = setInterval(async () => {
      attempts++
      try {
        const result = await verifyOrder(pendingRef)
        if (result.status === 'captured') {
          clearInterval(interval)
          useCartStore.getState().items.forEach((line) => addOwned(line.perfumeId))
          clearCart()
          addToast('Payment confirmed. Welcome to Little Sky.', 'success')
          navigate(`/order-status?reference=${pendingRef}`)
          return
        }
        if (result.status === 'failed' || result.status === 'abandoned') {
          clearInterval(interval)
          setVerifying(false)
          setError(`Payment ${result.status}. Please try again.`)
          setSearchParams({}, { replace: true })
        }
      } catch {
        // ignore transient errors and keep polling
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval)
        setVerifying(false)
        setError('We could not confirm your payment yet. Check your account for updates.')
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [pendingRef, clearCart, addOwned, addToast, navigate, setSearchParams])

  const addressReady = form.street && form.city && form.state

  useEffect(() => {
    if (!form.state) {
      setDelivery(null)
      setQuotes(null)
      setQuotesError(null)
      setSelectedQuote(null)
      return
    }

    const orderItems = items
      .map((item) => {
        const product = products.find((p) => p.id === item.perfumeId)
        const size = product?.sizes.find((s) => s.id === item.sizeId)
        if (!product?.apiId || !size) return null
        return {
          productId: product.apiId,
          variantId: Number(size.id),
          quantity: item.quantity,
        }
      })
      .filter(Boolean) as { productId: number; variantId: number; quantity: number }[]

    const timer = setTimeout(async () => {
      setDeliveryLoading(true)
      setQuotesLoading(true)
      setQuotesError(null)
      try {
        // Always load the static zone estimate first so the shopper has a
        // fallback when live carrier rates are unavailable (demo keys, missing
        // pickup address, or network blips).
        const estimate = await estimateDelivery({
          state: form.state,
          city: form.city || undefined,
          lga: form.lga || undefined,
        })
        setDelivery(estimate)

        if (addressReady && orderItems.length > 0) {
          const quoteResult = await getLogisticsQuotes({
            address: {
              street: form.street,
              city: form.city,
              state: form.state,
              lga: form.lga || undefined,
              country: 'NG',
            },
            items: orderItems,
          })
          if (quoteResult?.quotes.length) {
            setQuotes(quoteResult.quotes)
            // Prefer the cheapest option by default; the shopper can override.
            const cheapest = [...quoteResult.quotes].sort((a, b) => a.feeKobo - b.feeKobo)[0]
            setSelectedQuote(cheapest)
          } else {
            setQuotes([])
            // If live quotes are empty but a static estimate exists, fall back
            // to it silently. The shopper can still check out.
            setSelectedQuote(null)
          }
        }
      } catch (err) {
        // Live quotes failed. Keep any static estimate we already have and
        // surface a gentle message so the shopper isn't left without options.
        setQuotes([])
        setQuotesError(err instanceof Error ? err.message : 'Live carrier rates are unavailable.')
      } finally {
        setDeliveryLoading(false)
        setQuotesLoading(false)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [form.street, form.city, form.state, form.lga, items, products, addressReady])

  // The item subtotal in kobo. Quoted to the discount API, which prices a code
  // against exactly the same figure the order path will.
  const itemsKobo = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100,
    [items]
  )

  // Keep the quote honest as the basket changes. A code that stops applying
  // (the subtotal fell under its minimum, say) drops itself rather than
  // silently charging a figure the server would refuse.
  useEffect(() => {
    if (!applied) return
    let cancelled = false
    previewDiscount(applied.code, itemsKobo).then((result) => {
      if (cancelled) return
      if (result.success) {
        setApplied({
          code: result.discount.code,
          label: result.discount.label || result.discount.code,
          amountKobo: result.discount.amountKobo,
        })
      } else {
        setApplied(null)
      }
    })
    return () => {
      cancelled = true
    }
  }, [itemsKobo, applied?.code])

  const discountNaira = (applied?.amountKobo ?? 0) / 100
  const selectedFee = selectedQuote?.fee ?? delivery?.fee ?? 0
  const payable = Math.max(0, total() + selectedFee - discountNaira)

  if (items.length === 0 && !pendingRef) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-3xl text-carbon">Your bag is empty</h1>
      </Container>
    )
  }

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const orderItems = items
      .map((item) => {
        const product = products.find((p) => p.id === item.perfumeId)
        const size = product?.sizes.find((s) => s.id === item.sizeId)
        if (!product?.apiId || !size) return null
        return {
          productId: product.apiId,
          variantId: Number(size.id),
          quantity: item.quantity,
        }
      })
      .filter(Boolean) as { productId: number; variantId: number; quantity: number }[]

    if (orderItems.length !== items.length) {
      setError('Some items could not be matched to the catalogue. Please refresh and try again.')
      setLoading(false)
      return
    }

    try {
      const result = await createOrder({
        email: form.email,
        name: form.name || undefined,
        phone: form.phone || undefined,
        address: {
          street: form.street,
          city: form.city,
          state: form.state,
          lga: form.lga || undefined,
          country: 'NG',
        },
        items: orderItems,
        discountCode: applied?.code,
        callbackUrl: `${window.location.origin}/checkout`,
        quote: selectedQuote
          ? {
              quoteId: selectedQuote.quoteId,
              provider: selectedQuote.provider,
              serviceLevel: selectedQuote.serviceLevel,
              feeKobo: selectedQuote.feeKobo,
              estimatedDays: selectedQuote.estimatedDays,
            }
          : undefined,
      })

      if (!result.success) {
        setError(result.error)
        setLoading(false)
        return
      }

      window.location.href = result.authorizationUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout.')
      setLoading(false)
    }
  }

  const accordions = [
    {
      id: 'shipping',
      title: 'Shipping estimate',
      content: (
        <div className="space-y-2 text-sm">
          {quotesLoading ? (
            <p className="text-carbon/60">Fetching live carrier rates…</p>
          ) : quotes && quotes.length > 0 ? (
            quotes.map((q) => (
              <div key={q.quoteId} className="flex justify-between">
                <span>
                  {q.provider} — {q.serviceLevel}
                </span>
                <span>
                  {formatPrice(q.fee)} ({q.estimatedDays} day{q.estimatedDays === 1 ? '' : 's'})
                </span>
              </div>
            ))
          ) : delivery ? (
            <div className="flex justify-between">
              <span>Standard delivery</span>
              <span>
                {formatPrice(delivery.fee)} ({delivery.min_days}–{delivery.max_days} days)
              </span>
            </div>
          ) : (
            <p className="text-carbon/60">Enter your full address to see live rates.</p>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-5xl">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-wd-orange">Secure checkout</p>
        <h1 className="mb-8 font-display text-4xl normal-case text-carbon">checkout</h1>

        {verifying && (
          <div className="mb-6 bg-wd-mushroom p-4 text-sm text-carbon">
            Confirming payment with Paystack…
          </div>
        )}
        {error && (
          <div className="mb-6 border border-line bg-card p-4 text-sm text-carbon">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid gap-12 lg:grid-cols-5">
          <div className="space-y-8 lg:col-span-3">
            <section className="bg-wd-mist p-6">
              <h2 className="mb-4 font-display text-lg normal-case text-carbon">Contact & Shipping</h2>
              <div className="grid gap-6 md:grid-cols-2">
                <Input
                  label="Full name"
                  required
                  placeholder="Ada Obi"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  placeholder="ada@example.com"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
                <Input
                  label="Phone"
                  placeholder="+234 800 000 0000"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
                <Input
                  label="Street"
                  required
                  placeholder="12 Admiralty Way"
                  value={form.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                />
                <Input
                  label="City"
                  required
                  placeholder="Lekki"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
                <Input
                  label="State"
                  required
                  placeholder="Lagos"
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                />
                <Input
                  label="LGA (optional)"
                  placeholder="Ikeja"
                  value={form.lga}
                  onChange={(e) => handleChange('lga', e.target.value)}
                />
              </div>
            </section>

            <DiscountBox itemsKobo={itemsKobo} applied={applied} onChange={setApplied} />

            <section className="bg-wd-mist p-6">
              <h2 className="mb-4 font-display text-lg normal-case text-carbon">Payment</h2>
              <p className="text-sm text-carbon/70">
                You will be redirected to Paystack to complete payment securely by card, bank transfer, or USSD.
              </p>
            </section>

            <Accordion items={accordions} />
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-28 bg-wd-mist p-6">
              <h2 className="mb-4 font-display text-lg normal-case text-carbon">Order summary</h2>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-carbon/60">
                      {item.perfumeName.toLowerCase()} · {item.sizeLabel} × {item.quantity}
                    </span>
                    <span className="text-carbon">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              {giftWrap && (
                <div className="mt-4 flex justify-between text-sm text-carbon/60">
                  <span>Gift wrap</span>
                  <span>{formatPrice(2500)}</span>
                </div>
              )}

              <div className="mt-6 space-y-3">
                <h3 className="font-display text-sm normal-case text-carbon">Choose delivery</h3>
                {quotesLoading || deliveryLoading ? (
                  <p className="text-sm text-carbon/60">Estimating delivery…</p>
                ) : quotes && quotes.length > 0 ? (
                  quotes.map((q) => {
                    const isSelected = selectedQuote?.quoteId === q.quoteId
                    return (
                      <button
                        key={q.quoteId}
                        type="button"
                        onClick={() => setSelectedQuote(q)}
                        className={`flex w-full items-center justify-between rounded border p-3 text-left text-sm transition-colors ${
                          isSelected
                            ? 'border-carbon bg-carbon/5'
                            : 'border-ink/10 hover:border-carbon/30'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span
                            className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                              isSelected ? 'border-carbon' : 'border-carbon/40'
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </span>
                          <span className="text-carbon">
                            {q.provider}
                            <span className="block text-xs text-carbon/60">{q.serviceLevel}</span>
                          </span>
                        </span>
                        <span className="text-right text-carbon">
                          {formatPrice(q.fee)}
                          <span className="block text-xs text-carbon/60">{q.estimatedDays} day{q.estimatedDays === 1 ? '' : 's'}</span>
                        </span>
                      </button>
                    )
                  })
                ) : delivery ? (
                  <>
                    <div className="flex justify-between rounded border border-ink/10 p-3 text-sm">
                      <span className="text-carbon">Standard delivery</span>
                      <span className="text-carbon">
                        {formatPrice(delivery.fee)} ({delivery.min_days}–{delivery.max_days} days)
                      </span>
                    </div>
                    {quotesError && (
                      <p className="text-xs text-carbon/50">
                        Live carrier rates are temporarily unavailable. Showing our standard delivery estimate.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-carbon/60">
                    {quotesError
                      ? 'We could not fetch delivery options for this address. Please check the address or contact us.'
                      : 'Enter your address to see delivery options.'}
                  </p>
                )}
              </div>

              <div className="mt-6 border-t border-ink/10 pt-4">
                {applied && (
                  <div className="mb-3 flex justify-between gap-3 text-sm text-carbon">
                    <span className="min-w-0 truncate">{applied.label}</span>
                    <span className="shrink-0">
                      {discountNaira > 0 ? `−${formatPrice(discountNaira)}` : '—'}
                    </span>
                  </div>
                )}
                <div className="mb-3 flex items-start gap-2 text-xs text-carbon/60">
                  <Truck className="h-4 w-4 shrink-0" />
                  <span>Free shipping over ₦30,000.</span>
                </div>
                <div className="flex justify-between text-lg text-carbon">
                  <span className="font-display">Total</span>
                  <span className="font-display">{formatPrice(payable)}</span>
                </div>
              </div>

              <Button type="submit" isLoading={loading} className="mt-6 w-full">
                Pay {formatPrice(payable)}
              </Button>
            </div>
          </div>
        </form>
      </Container>
    </div>
  )
}
